<template>
<div
  :class="[
    'favorites',
    { empty: !favorites.length, 'screen-lg': this.onTabletUp && this.currentPrimaryView.component === 'Favorites' },
    this.currentView.context,
  ]"
>
  <div class="section-title">
    <h3 v-if="this.currentPrimaryView.component === 'Favorites'">
      {{ $t('favorite.title') }}
      <small>{{ $t('favorite.subheading') }}</small>
    </h3>
  </div>

  <transition-group class="recipe-items-wrapper" name="list-complete" tag="div" v-if="favorites.length">
    <MealCard
      class="list-complete-item"
      v-for="(dish, i) in favorites"
      :key="dish.type + '-' + dish.id"
      :dish="dish"
      :cardType="'meal'"
      :onTabletUp="onTabletUp"
      @showDishDetails="showDishDetails"
    />
  </transition-group>

  <div class="notice-empty" v-if="!favorites.length">
    <img class="img-heart" src="@assets/images/img-heart.png" />
    <h2>{{ $t('favorite.notFound') }}</h2>
    <h4>{{ $t('favorite.notFoundDescription') }}</h4>
    <BaseButton @click="toRecipes">{{ $t('favorite.btnBrowseRecipes') }}</BaseButton>
  </div>
</div>
</template>

<script>
import store from '@state/store';
import MealCard from '../MealCard/MealCard';
import utils from '@src/utils/utils';
import { gtmMixin } from 'gtmMixin';
import {
  favoritesComputed,
  favoritesMethods,
  kitchenDrawerComputed,
  kitchenDrawerMethods,
  mealsComputed,
  mealsMethods,
  primaryNavComputed,
  primaryNavMethods,
  recipesComputed,
  recipesMethods,
} from '@state/helpers';

export default {
  mixins: [gtmMixin],
  components: {
    MealCard,
  },
  props: {
    isEmbed: {
      type: Boolean,
    },
    deepLink: {
      type: String,
    },
    onTabletUp: {
      type: Boolean,
    },
  },
  data() {
    return {
      dishes: [],
    };
  },
  computed: {
    ...favoritesComputed,
    ...kitchenDrawerComputed,
    ...mealsComputed,
    ...primaryNavComputed,
    ...recipesComputed,
    currentView() {
      return this.navContent[this.activeNavIndex];
    },
    currentPrimaryView() {
      return this.primaryNavContent[this.activePrimaryNavIndex];
    },
  },
  async mounted() {
    this.gtmTrackEvent('pageView', { action: 'Visit Favorites', label: 'Favorites View' });
    await this.getFavorites();

    if (this.deepLink) {
      const deepLinkParts = utils.splitWithRemainder(this.deepLink, '/', 2);
      if (deepLinkParts && deepLinkParts.length > 0) {
        const dishId = parseInt(deepLinkParts[1].toLowerCase());

        if (deepLinkParts[0].toLowerCase() === 'r') {
          this.navigateIntoRecipe(dishId);
        } else if (deepLinkParts[0].toLowerCase() === 'm') {
          this.navigateIntoMeal(dishId);
        }
      }
    }
  },
  methods: {
    ...favoritesMethods,
    ...mealsMethods,
    ...primaryNavMethods,
    ...recipesMethods,
    ...kitchenDrawerMethods,
    async navigateIntoRecipe(dishId) {
      if (!this.cachedRecipesMap[dishId]) {
        await this.getRecipe(dishId);
      }

      this.navigateDeeper({
        title: this.$t('lblRecipe'),
        component: 'RecipeDetail',
        data: this.cachedRecipesMap[dishId],
        context: this.onTabletUp ? 'drawerModal' : null,
      });

      if (this.onTabletUp && this.currentView.context !== 'drawerModal') {
        this.toggleDrawer();
      }
    },
    async navigateIntoMeal(dishId) {
      if (!this.cachedMeal[dishId]) {
        await this.getMeal(dishId);
      }

      this.navigateDeeper({
        title: 'Meal',
        component: 'MealDetail',
        data: this.cachedMeal[dishId],
        context: this.onTabletUp ? 'drawerModal' : null,
      });

      if (this.onTabletUp && this.currentView.context !== 'drawerModal') {
        this.toggleDrawer();
      }
    },
    async showDishDetails(dish) {
      if (dish.type === 'r') {
        await this.navigateIntoRecipe(dish.id);
      } else if (dish.type === 'm') {
        await this.navigateIntoMeal(dish.id);
      }
    },
    toRecipes() {
      if (this.onTabletUp && this.currentView.context !== 'drawerModal') {
        this.navigateDeeperPrimary({
          title: 'Recipes',
          component: 'Recipes',
          primary: true,
          isActive: true,
        });
      } else {
        this.navigateDeeper({
          title: 'All Recipes',
          component: 'Recipes',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'theme.scss';

.favorites {
  @extend %scrollable-drawer-content;

  padding: 0.6rem 0.6rem 11rem;

  .list-complete-item {
    margin-right: 10px;
    transition: all 0.5s;
    width: 100%;
  }

  .list-complete-enter,
  .list-complete-leave-to {
    opacity: 0;
    transform: translateY(-30rem);
  }

  .list-complete-leave-active {
    position: absolute;
  }

  &.empty {
    align-items: center;
    display: flex;

    .notice-empty {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: space-evenly;
      padding: 0 2rem;
      text-align: center;

      h2,
      h4,
      .img-heart {
        margin-bottom: 2.6rem;
      }

      .img-heart {
        max-width: 25rem;
        width: 50%;
      }

      h4 {
        line-height: 1.8rem;
      }
    }
  }

  &.screen-lg {
    display: flex;
    flex-direction: column;
    position: relative;

    .section-title {
      border-top: 4px solid $color-navbar-bg;
      margin: 2rem 0 0 3rem;
      padding: 1rem 0;
      width: 75%;

      h3 {
        line-height: 1.3;
        small {
          display: block;
        }
      }
    }

    .recipe-items-wrapper {
      align-content: start;
      align-items: auto;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      height: 107vh;
      justify-content: start;
      overflow-y: scroll;
      padding: 1rem 0 1rem 3rem;
      width: 100%;
    }

    .empty-notice {
      width: 100%;
    }

    &.empty {
      display: block;
    }
  }

  &.drawerModal {
    height: 100%;
  }
}
</style>
